import { useTheme } from '@emotion/react';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { AxiosResponse } from 'axios';
import { FC, KeyboardEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useNavigate } from 'react-router-dom';
import Checkbox from '../Checkbox/Checkbox';
import Link from '../Link/Link';
import { Loading } from '../Loading/Loading';
import { Row, RowLabel, StyledTreeViewContainer } from './TreeView.styles';
import { RowProps, TreeViewProps } from './TreeView.types';

const LoadingRow = ({ level }: { level: number }) => (
  <Row level={level} data-testid='LoadingRow'>
    <Loading size={12} />
  </Row>
);

const TreeView: React.FC<TreeViewProps> = ({
  rows,
  level = 0,
  checkedRows,
  expandedRows,
  onRowClick,
  onCheckboxClick,
  dataStructure,
  childRowsChecked = false,
  parentId,
  parentRows,
  expandOnCheck = false,
  noRowsMessage = 'No records found.',
  checkEveryRow = false,
  handleRowsProps,
  handleHasUpdate,
  hasUpdate
}) => {

  const idKey = dataStructure[level]?.idKey;
  const isFirst = useRef(true);

  useEffect(() => {
    if (!isFirst.current && onCheckboxClick) {
      rows.forEach(row => {
        let newParentRows = parentRows ? [...parentRows, { id: row[idKey], level }] : [{ id: row[idKey], level }];
        onCheckboxClick({ ...row, level, rowChecked: checkEveryRow, parentId: undefined, parentRows: newParentRows })
      });
    }
  }, [checkEveryRow]);

  useEffect(() => {
    isFirst.current = false;
  }, []);

  useEffect(() => {
    if (rows && rows.length > 0 && handleRowsProps) {
      handleRowsProps(rows);
    }
  }, [hasUpdate]);

  return (
    <>
      {rows?.map((row: any) => {
        const newParentRows = parentRows ? [...parentRows, { id: row[idKey], level }] : [{ id: row[idKey], level }];
        return (
          <TreeViewRow
            dataStructure={dataStructure}
            key={row[idKey]}
            level={level}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            onRowClick={onRowClick}
            onCheckboxClick={onCheckboxClick}
            row={row}
            rowCheckedFromParent={childRowsChecked}
            parentId={parentId}
            parentRows={newParentRows}
            expandOnCheck={expandOnCheck}
            handleAlertParent={handleHasUpdate}
          />
        );
      })}
      {(!rows || rows.length === 0) && (
        <Row level={level + 1}>
          <RowLabel>{noRowsMessage}</RowLabel>
        </Row>
      )}
    </>
  );
};

const TreeViewRow: FC<RowProps> = ({
  onRowClick,
  onCheckboxClick,
  level,
  checkedRows,
  expandedRows,
  dataStructure,
  row,
  rowCheckedFromParent = false,
  parentId,
  parentRows,
  expandOnCheck,
  handleAlertParent
}) => {
  const { fetchChildren, idKey, buildLabel, to, buildRequestBody, showCheckboxes } = dataStructure[level];
  const { idKey: childIdKey } = dataStructure[level + 1] || { idKey: false };
  const label = buildLabel({ parentId, row, dataStructure, level, parentRows });
  const id = row[idKey];
  const navigate = useNavigate();
  const theme = useTheme();

  const isInExpandedRows = () => {
    if (expandedRows?.length === 0) return false;
    return (
      expandedRows?.findIndex(
        (expandedRow: any) =>
          expandedRow[idKey] === id && expandedRow.level === level && isEqual(expandedRow?.parentRows, parentRows)
      ) !== -1
    );
  };

  const isInCheckedRows = () => {
    if (checkedRows?.length === 0) return false;
    return (
      checkedRows?.findIndex(
        (checkedRow: any) =>
          checkedRow[idKey] === id && checkedRow.level === level && isEqual(checkedRow?.parentRows, parentRows)
      ) !== -1
    );
  };

  const [children, setChildren] = useState<any[]>([]);
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const rowIsInCheckedRows = useMemo(isInCheckedRows, [checkedRows?.length]);
  const rowIsInExpandedRows = useMemo(isInExpandedRows, [expandedRows?.length]);

  const defaultChecked = rowCheckedFromParent || rowIsInCheckedRows || false;

  const defaultExpanded = expandOnCheck ? defaultChecked || rowIsInExpandedRows || false : rowIsInExpandedRows || false;

  const [rowExpanded, setRowExpanded] = useState(defaultExpanded);
  const [rowChecked, setRowChecked] = useState(defaultChecked);
  const [rowIndeterminate, setRowIndeterminate] = useState(false);


  const handleRowsProps = (rows: any[]) => {
    setChildren(rows);
  }
  const handleHasUpdate = () => {
    setHasUpdate(!hasUpdate);
    if (handleAlertParent) handleAlertParent();
  }

  useEffect(() => {
    setRowExpanded(rowIsInExpandedRows);
  }, [rowIsInExpandedRows]);

  useEffect(() => {
    row.checked = rowChecked;
    if (handleAlertParent) handleAlertParent();
  }, [rowChecked]);

  useEffect(() => {
    row.checked = rowChecked;
  }, []);

  useEffect(() => {
    if (children.length > 0 && childIdKey && !rowChecked) {
      let childrenLength = children.length;
      let checkedAcc = 0;
      children.forEach(child => {
        if (child.checked) checkedAcc++;
      });
      if (childrenLength === checkedAcc) {
        handleCheckboxClick();
        setRowChecked(true);
      }
    }
  }, [hasUpdate]);

  useEffect(() => {
    if (rowCheckedFromParent) {
      setRowChecked(true);
      // automatically expands the row when checked from parent if expandOnCheck is true
      if (expandOnCheck) {
        setRowExpanded(true);
        if (onRowClick) onRowClick({ ...row, level, rowExpanded: true, parentId, parentRows });
      }
      if (onCheckboxClick) onCheckboxClick({ ...row, level, rowChecked: true, parentId, parentRows });
    } else if (!rowIsInCheckedRows) {
      setRowChecked(false);
    }
  }, [rowCheckedFromParent]);

  useEffect(() => {
    if (rowIsInCheckedRows) {
      // automatically expands the row when added to checkedRows if expandOnCheck is true
      if (expandOnCheck) {
        setRowExpanded(true);
        if (onRowClick) onRowClick({ ...row, level, rowExpanded: true, parentId, parentRows });
      }
      setRowChecked(true);
    } else {
      setRowChecked(false);
    }
  }, [rowIsInCheckedRows]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.target as HTMLElement).tagName === 'INPUT') return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        handleRowClick();
        break;
      default:
        return;
    }
  };

  const handleRowClick = (event?: MouseEvent) => {
    if ((event?.target as HTMLElement)?.tagName === 'INPUT') return;
    if (onRowClick) onRowClick({ ...row, level, rowExpanded: !rowExpanded, parentId, parentRows });
    setRowExpanded(!rowExpanded);
  };

  // Cleans up children rows to initial state if row is unchecked and it is not expanded
  const clearChildren = () => {
    const nextChildren = children.map(value => {
      value.checked = false;
      value.indeterminate = false;
      return value;
    });
    setChildren(nextChildren);
    setRowIndeterminate(false);
  }

  const handleCheckboxClick = () => {
    if (onCheckboxClick) onCheckboxClick({ ...row, level, rowChecked: !rowChecked, parentId, parentRows });
    // automatically expands the row when checked from parent if expandOnCheck is true
    if (expandOnCheck && !rowChecked) {
      setRowExpanded(true);
      if (onRowClick) onRowClick({ ...row, level, rowExpanded: true, parentId, parentRows });
    }
    if (!rowChecked && !isInExpandedRows()) {
      clearChildren();
    }
    setRowChecked(!rowChecked);
  };

  if (fetchChildren && buildRequestBody) {
    const { data, isLoading, isFetched } = fetchChildren(
      buildRequestBody({ parentId, row, dataStructure, level, parentRows }),
      rowExpanded
    );
    const childRows = children.length >= (data as AxiosResponse)?.data?.length ? children : (data as AxiosResponse)?.data;

    const strLabel = idKey === 'marketNo' || idKey === 'storeNo' ? `${label}`: `${label} ~ ${id}`
    return (
      <>
        <Row
          tabIndex={0}
          level={level + 1}
          onClick={handleRowClick}
          onKeyDown={handleKeyDown}
          data-testid='TreeViewRow'
        >
          {rowExpanded ? (
            <ExpandMoreOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          ) : (
            <ChevronRightOutlinedIcon htmlColor={theme.colors.gray600} sx={{ margin: '0 8px' }} />
          )}
          {showCheckboxes && (
            <Checkbox
              checked={rowChecked}
              label=''
              id={id}
              onClick={handleCheckboxClick}
              readOnly
              aria-label='check row'
              indeterminate={rowIndeterminate && !rowChecked}
            />
          )}
          <RowLabel level={level} expanded={rowExpanded}>
            {typeof label === 'string' ? strLabel : label}
          </RowLabel>
        </Row>
        {isFetched && rowExpanded ? (
          <TreeView
            dataStructure={dataStructure}
            rows={childRows}
            childRowsChecked={rowChecked}
            level={level + 1}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            onRowClick={onRowClick}
            onCheckboxClick={onCheckboxClick}
            parentId={id}
            parentRows={parentRows}
            expandOnCheck={expandOnCheck}
            handleRowsProps={handleRowsProps}
            handleHasUpdate={handleHasUpdate}
            hasUpdate={hasUpdate}
          />
        ) : isLoading && rowExpanded ? (
          <LoadingRow level={level ? level + 1 : 1} />
        ) : null}
      </>
    );
  }
  if (to) {
    let stringTo: string;
    let handleLinkRowClick: (event: MouseEvent) => void;

    const toReturnValue = to({ parentId, row, dataStructure, level, parentRows });
    // to can either return a string or an object (which allows passing navigateOptions)
    if (typeof toReturnValue === 'string') {
      stringTo = toReturnValue;
      handleLinkRowClick = (event: MouseEvent) => {
        if ((event?.target as HTMLElement).tagName === 'INPUT') return;
        navigate(stringTo);
      };
    } else {
      const { navigateTo, navigateOptions } = toReturnValue;
      stringTo = navigateTo;
      handleLinkRowClick = (event: MouseEvent) => {
        if ((event?.target as HTMLElement).tagName === 'INPUT') return;
        navigate(stringTo, navigateOptions);
      };
    }
    return (
      <>
        <Row level={level + 1} data-link={true} onClick={handleLinkRowClick} data-testid='TreeViewRow'>
          {showCheckboxes && (
            <Checkbox checked={rowChecked} label='' id={id} onClick={handleCheckboxClick} readOnly />
          )}
          <RowLabel level={level}>{<Link to={stringTo} label={label} />}</RowLabel>
        </Row>
      </>
    );
  }
  return null;
};

// provides the border & size, outermost component that is imported in each page
const TreeViewContainer: FC<TreeViewProps> = (props) => (
  <StyledTreeViewContainer borderRadius={props.borderRadius}>
    <TreeView {...props} />
  </StyledTreeViewContainer>
);

export default TreeViewContainer;
